import React, { useState, useEffect, useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import theme from "../../themes";
import { Card, Button, Spin, App, Tooltip, Select, Empty } from "antd";
import {
  SendOutlined,
  PaperClipOutlined,
  LoadingOutlined,
  CloseOutlined,
  LeftOutlined,
  HistoryOutlined,
  DownOutlined,
  UpOutlined,
  PhoneOutlined,
  MessageOutlined,
  MailOutlined,
  WhatsAppOutlined,
  CheckOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { Input } from "antd";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getWhatsappConversationByIdThunk,
  sendWhatsappMessageThunk,
  sendWhatsappFilesThunk,
  updateWhatsappConversationStatusThunk,
} from "../../store/actions/whatsapp.action";
import { clearCurrentConversation } from "../../store/reducers/whatsapp.reducer";
import { getWhatsappConversationById } from "../../services/whatsapp.service";
import useThreadPolling from "../../hooks/useThreadPolling";
import { IWhatsappConversation, IWhatsappMessage } from "../../models/whatsapp.model";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import {
  WHATSAPP_ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  getFileIcon,
  formatFileSize,
  isAllowedWhatsappFile,
} from "../../utils/fileUtils";
import { ThreadMedia } from "../../components/ThreadMedia";
import {
  TICKET_DISPLAY_STATUS,
  displayStatusOf,
  isAiHandling,
  isNeedsAssign,
  parseInboxStatus,
  statusFieldsForDisplay as buildStatusFields,
} from "../../utils/ticketStatus";

const { TextArea } = Input;

type TicketDisplayStatus =
  | typeof TICKET_DISPLAY_STATUS.OPEN
  | typeof TICKET_DISPLAY_STATUS.NEED_ASSIGN
  | typeof TICKET_DISPLAY_STATUS.FAILED
  | typeof TICKET_DISPLAY_STATUS.COMPLETED;

const statusColors: Record<string, { text: string; background: string }> = {
  open: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  need_assign: {
    text: "#E65100",
    background: "#FFF3E0",
  },
  escalated: {
    text: "#E65100",
    background: "#FFF3E0",
  },
  failed: {
    text: theme.colors.red1,
    background: "#FFEBEE",
  },
  completed: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
};

const formatPhone = (value?: string): string => {
  if (!value) return "";
  return value.replace(/^whatsapp:/i, "").trim();
};

const formatClock = (isoString?: string, locale?: string): string => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(locale || "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatShortDate = (isoString?: string, locale?: string): string => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale || "en-GB", {
    day: "numeric",
    month: "short",
  });
};

const formatLongDate = (isoString?: string, locale?: string): string => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale || "en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

const formatCallDuration = (seconds?: number): string => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return "";
  const mins = Math.floor(value / 60);
  const secs = Math.floor(value % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const normalizeChannel = (channel?: string): string => {
  const value = String(channel || "").toLowerCase();
  if (value === "sms" || value === "text") return "sms";
  if (value === "whatsapp") return "whatsapp";
  if (value === "email" || value === "mail") return "email";
  if (value === "call" || value === "phone") return "call";
  return value;
};

const channelIconOf = (channel?: string) => {
  const value = normalizeChannel(channel);
  if (value === "call") return <PhoneOutlined />;
  if (value === "sms") return <MessageOutlined />;
  if (value === "email") return <MailOutlined />;
  if (value === "whatsapp") return <WhatsAppOutlined />;
  return <MessageOutlined />;
};

type ChannelCounts = {
  sms?: number;
  whatsapp?: number;
  email?: number;
  call?: number;
};

const CHANNEL_ICON_ORDER = ["call", "email", "whatsapp", "sms"] as const;

const countsFromMessages = (messages?: IWhatsappMessage[]): ChannelCounts => {
  const counts: ChannelCounts = {};
  for (const message of messages || []) {
    const channel = normalizeChannel(message.channel);
    if (!channel) continue;
    counts[channel as keyof ChannelCounts] =
      (counts[channel as keyof ChannelCounts] || 0) + 1;
  }
  return counts;
};

/** Inbox sender: stored direction wins so campaign blasts are never "Customer". */
const isCustomerMessage = (msg: IWhatsappMessage): boolean => {
  if (msg.direction === "outbound") return false;
  if (msg.direction === "inbound") return true;
  return msg.role === "user" || msg.actor === "customer";
};

const primaryChannelOf = (
  counts?: ChannelCounts,
  used?: string[],
  fallback?: string
): string => {
  for (const channel of CHANNEL_ICON_ORDER) {
    if ((counts?.[channel] || 0) > 0) return channel;
    if ((used || []).some((item) => normalizeChannel(item) === channel)) {
      return channel;
    }
  }
  return normalizeChannel(fallback) || "sms";
};

const normalizeDisplayStatus = (
  status?: string,
  conversation?: IWhatsappConversation | { status?: string; state?: string; resolution?: string | null }
): TicketDisplayStatus => {
  if (conversation) return displayStatusOf(conversation);
  return parseInboxStatus(status) as TicketDisplayStatus;
};

const isNeedsReply = (conversation: IWhatsappConversation): boolean =>
  isNeedsAssign(conversation);

const isClosedStatus = (status?: string, conversation?: IWhatsappConversation): boolean => {
  const display = conversation
    ? displayStatusOf(conversation)
    : (parseInboxStatus(status) as TicketDisplayStatus);
  return (
    display === TICKET_DISPLAY_STATUS.COMPLETED ||
    display === TICKET_DISPLAY_STATUS.FAILED
  );
};

const isAiHandlingConversation = (
  conversation: IWhatsappConversation
): boolean => isAiHandling(conversation);

type ConversationStatusPatch = {
  _id: string;
  status?: IWhatsappConversation["status"];
  handling_mode?: IWhatsappConversation["handling_mode"];
  awaiting?: IWhatsappConversation["awaiting"];
  state?: IWhatsappConversation["state"];
  assigned_to?: IWhatsappConversation["assigned_to"];
  resolution?: IWhatsappConversation["resolution"];
  internal_status?: IWhatsappConversation["internal_status"];
};

const WhatsappDetail: React.FC<{
  embedded?: boolean;
  conversationId?: string;
  campaignId?: string;
  onConversationUpdated?: (patch: ConversationStatusPatch) => void;
}> = ({ embedded = false, conversationId, campaignId, onConversationUpdated }) => {
  const { t, i18n } = useTranslation();
  const { id: routeId } = useParams<{ id: string }>();
  const id = conversationId || routeId;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [openHistId, setOpenHistId] = useState<string | null>(null);
  const [histMessages, setHistMessages] = useState<
    Record<string, IWhatsappMessage[]>
  >({});
  const [histLoadingId, setHistLoadingId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<Map<string, string>>(
    new Map()
  );
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replyInputRef = useRef<any>(null);
  const focusReplyAfterTakeoverRef = useRef(false);
  const prevMessageCountRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  const { currentConversation, loading } = useAppSelector(
    (state) => state.whatsapp
  );

  useEffect(() => {
    isInitialLoadRef.current = true;
    prevMessageCountRef.current = 0;
    setShowHistory(false);
    setOpenHistId(null);
    setHistMessages({});
    setHistLoadingId(null);

    if (id) {
      // Opening the thread is the only thing that marks it read; the polls below
      // deliberately do not, so an incoming reply stays unread until it's seen.
      dispatch(
        getWhatsappConversationByIdThunk({
          id,
          markRead: true,
          campaign_id: campaignId,
        })
      );
    }

    return () => {
      dispatch(clearCurrentConversation());
    };
  }, [id, campaignId, dispatch]);

  useThreadPolling(
    () => {
      if (id) {
        dispatch(
          getWhatsappConversationByIdThunk({ id, campaign_id: campaignId })
        );
      }
    },
    {
      enabled: Boolean(id),
      activitySignal: currentConversation?.messages?.length,
    }
  );

  const scrollThreadToBottom = (smooth = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  useEffect(() => {
    const currentMessageCount = currentConversation?.messages?.length || 0;

    if (isInitialLoadRef.current && currentMessageCount > 0) {
      scrollThreadToBottom(false);
      isInitialLoadRef.current = false;
    } else if (currentMessageCount > prevMessageCountRef.current) {
      scrollThreadToBottom(true);
    }

    prevMessageCountRef.current = currentMessageCount;
  }, [currentConversation?.messages]);

  useEffect(() => {
    if (!focusReplyAfterTakeoverRef.current) return;
    if (!currentConversation || isAiHandlingConversation(currentConversation)) {
      return;
    }
    focusReplyAfterTakeoverRef.current = false;
    const timer = window.setTimeout(() => {
      replyInputRef.current?.focus?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [currentConversation]);

  const handleSendReply = async () => {
    if (
      sending ||
      (!replyMessage.trim() && selectedFiles.length === 0) ||
      !currentConversation
    ) {
      return;
    }

    const to = formatPhone(currentConversation.user_phone_number);
    const from = formatPhone(currentConversation.company_phone_number);
    if (!to || !from) {
      toast.error(t("inbound.whatsapp.missingPhoneToSend"), 5);
      return;
    }

    setSending(true);
    try {
      if (replyMessage.trim()) {
        await dispatch(
          sendWhatsappMessageThunk({
            message: replyMessage.trim(),
            to,
            from,
            conversation_id: currentConversation._id,
          })
        ).unwrap();
      }

      if (selectedFiles.length > 0) {
        await dispatch(
          sendWhatsappFilesThunk({
            files: selectedFiles,
            to,
            from,
            conversation_id: currentConversation._id,
          })
        ).unwrap();
        clearAllFiles();
      }

      setReplyMessage("");
      toast.success(
        t("inbound.whatsapp.messageSentSuccessfully"),
        5,
        <CheckCircleIcon />
      );

      if (id) {
        dispatch(
          getWhatsappConversationByIdThunk({ id, campaign_id: campaignId })
        );
      }
    } catch (error) {
      toast.error(t("inbound.whatsapp.failedToSendMessage"), 5);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: File[] = [];

    Array.from(files).forEach((file) => {
      if (!isAllowedWhatsappFile(file)) {
        toast.error(t("inbound.whatsapp.fileTypeNotSupported"), 5);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(t("inbound.whatsapp.fileTooLarge"), 5);
        return;
      }

      const fileKey = `${file.name}-${file.size}`;
      if (selectedFiles.some((f) => `${f.name}-${f.size}` === fileKey)) {
        return;
      }

      newFiles.push(file);

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviews((prev) => {
            const updated = new Map(prev);
            updated.set(fileKey, reader.result as string);
            return updated;
          });
        };
        reader.readAsDataURL(file);
      }
    });

    if (newFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (fileToRemove: File) => {
    const fileKey = `${fileToRemove.name}-${fileToRemove.size}`;
    setSelectedFiles((prev) =>
      prev.filter((f) => `${f.name}-${f.size}` !== fileKey)
    );
    setFilePreviews((prev) => {
      const updated = new Map(prev);
      updated.delete(fileKey);
      return updated;
    });
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setFilePreviews(new Map());
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const getStatusColor = (status: string) => {
    return (
      statusColors[status] || {
        text: theme.colors.grey1,
        background: theme.colors.grey5,
      }
    );
  };

  const getStatusLabel = (status: string) => {
    switch (normalizeDisplayStatus(status)) {
      case TICKET_DISPLAY_STATUS.OPEN:
        return t("inbound.whatsapp.statusOpen");
      case TICKET_DISPLAY_STATUS.NEED_ASSIGN:
        return t("inbound.whatsapp.needsAssign");
      case TICKET_DISPLAY_STATUS.FAILED:
        return t("inbound.whatsapp.statusFailed");
      case TICKET_DISPLAY_STATUS.COMPLETED:
        return t("inbound.whatsapp.statusCompleted");
      default:
        return status;
    }
  };

  const statusFieldsForDisplay = (
    newStatus: string
  ): Omit<ConversationStatusPatch, "_id"> => buildStatusFields(newStatus);

  const applyConversationStatus = async (
    newStatus: string,
    { successKey, errorKey }: { successKey: string; errorKey: string }
  ) => {
    if (!currentConversation || !id) return false;

    setUpdatingStatus(true);
    try {
      await dispatch(
        updateWhatsappConversationStatusThunk({
          id: currentConversation._id,
          status: newStatus,
        })
      ).unwrap();
      onConversationUpdated?.({
        _id: currentConversation._id,
        ...statusFieldsForDisplay(newStatus),
      });
      toast.success(t(successKey), 3);
      dispatch(
        getWhatsappConversationByIdThunk({ id, campaign_id: campaignId })
      );
      return true;
    } catch (error) {
      toast.error(t(errorKey), 5);
      return false;
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (
      !currentConversation ||
      newStatus === normalizeDisplayStatus(undefined, currentConversation)
    ) {
      return;
    }

    await applyConversationStatus(newStatus, {
      successKey: "inbound.whatsapp.statusUpdatedSuccessfully",
      errorKey: "inbound.whatsapp.failedToUpdateStatus",
    });
  };

  const handleTakeOver = async () => {
    if (!currentConversation || updatingStatus) return;
    if (!isAiHandlingConversation(currentConversation)) return;

    const ok = await applyConversationStatus(TICKET_DISPLAY_STATUS.NEED_ASSIGN, {
      successKey: "inbound.whatsapp.tookOverSuccessfully",
      errorKey: "inbound.whatsapp.failedToTakeOver",
    });
    if (ok) focusReplyAfterTakeoverRef.current = true;
  };

  const statusOptions = [
    {
      value: TICKET_DISPLAY_STATUS.OPEN,
      label: getStatusLabel(TICKET_DISPLAY_STATUS.OPEN),
    },
    {
      value: TICKET_DISPLAY_STATUS.NEED_ASSIGN,
      label: getStatusLabel(TICKET_DISPLAY_STATUS.NEED_ASSIGN),
    },
    {
      value: TICKET_DISPLAY_STATUS.FAILED,
      label: getStatusLabel(TICKET_DISPLAY_STATUS.FAILED),
    },
    {
      value: TICKET_DISPLAY_STATUS.COMPLETED,
      label: getStatusLabel(TICKET_DISPLAY_STATUS.COMPLETED),
    },
  ];

  const getDisplayMessages = () => {
    if (!currentConversation?.messages) return [];

    return currentConversation.messages.filter((msg, index) => {
      if (msg.actor === "system" && !(msg.content || "").trim()) return false;
      if (msg.role === "assistant" && !msg.created_at) return false;
      // Legacy threads stored the agent system prompt as the first long
      // assistant row. Campaign blasts and other real outbound sends use the
      // same shape, so only hide the prompt when it is not an outbound send.
      if (
        index === 0 &&
        msg.role === "assistant" &&
        (msg.content || "").length > 180 &&
        msg.direction !== "outbound" &&
        msg.actor !== "system"
      ) {
        return false;
      }
      return true;
    });
  };

  const getSenderName = (msg: IWhatsappMessage) => {
    if (isCustomerMessage(msg)) {
      return t("inbound.whatsapp.customer");
    }
    if (msg.actor === "human_agent") {
      return t("inbound.whatsapp.humanAgent");
    }
    return t("inbound.whatsapp.aiAgent");
  };

  const getChannelLabel = (channel?: string) => {
    const value = normalizeChannel(channel);
    if (value === "sms") return t("inbound.whatsapp.channelText");
    if (value === "whatsapp") return t("inbound.whatsapp.channelWhatsapp");
    if (value === "email") return t("inbound.whatsapp.channelEmail");
    if (value === "call") return t("inbound.whatsapp.channelCall");
    return "";
  };

  const formatChannelMix = (counts?: ChannelCounts) => {
    const parts = [
      counts?.call
        ? t("inbound.whatsapp.historyChannelCall", { count: counts.call })
        : "",
      counts?.sms
        ? t("inbound.whatsapp.historyChannelText", { count: counts.sms })
        : "",
      counts?.whatsapp
        ? t("inbound.whatsapp.historyChannelWhatsapp", { count: counts.whatsapp })
        : "",
      counts?.email
        ? t("inbound.whatsapp.historyChannelEmail", { count: counts.email })
        : "",
    ].filter(Boolean);
    return parts.join(", ");
  };

  const getMessageMeta = (msg: IWhatsappMessage, duration?: string) => {
    const parts = [
      getSenderName(msg),
      getChannelLabel(msg.channel),
      formatClock(msg.created_at, i18n.language),
      duration,
    ].filter(Boolean);
    return parts.join(" · ");
  };

  const getMessageStatus = (msg: IWhatsappMessage) => {
    if (msg.actor === "human_agent") return "humanTakeover" as const;
    if (msg.role !== "user" && msg.actor !== "system") {
      return "autonomous" as const;
    }
    return null;
  };

  const getSubjectFromMessages = () => {
    const subject = currentConversation?.subject?.trim() || "";
    const summary = currentConversation?.summary?.trim() || "";
    let fallback = "";
    if (!subject && !summary && currentConversation?.messages?.length) {
      const lastUserMessage = [...currentConversation.messages].find(
        (msg) => msg.role === "user"
      );
      fallback = lastUserMessage?.content || "";
    }
    const display =
      subject && summary && subject.length < summary.length
        ? subject
        : summary || subject || fallback;
    if (!display) return null;
    const tooltip =
      summary && summary !== display
        ? summary
        : display.length > 80
          ? display
          : "";

    return (
      <Tooltip title={tooltip || undefined} placement="topLeft">
        <span>{display}</span>
      </Tooltip>
    );
  };

  if (loading && !currentConversation) {
    return (
      <FlexRow $embedded={embedded}>
        <MainContent $embedded={embedded}>
          <LoadingContainer>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            />
          </LoadingContainer>
        </MainContent>
      </FlexRow>
    );
  }

  if (!currentConversation) {
    return (
      <FlexRow $embedded={embedded}>
        <MainContent $embedded={embedded}>
          {!embedded && (
            <BackButton onClick={() => navigate("/inbound")}>
              <LeftOutlined style={{ marginRight: 8 }} />
              {t("inbound.whatsapp.backToList")}
            </BackButton>
          )}
          <EmptyThread>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("inbound.whatsapp.conversationNotFound")}
            />
          </EmptyThread>
        </MainContent>
      </FlexRow>
    );
  }

  const displayMessages = getDisplayMessages();
  const threadDisplayStatus = normalizeDisplayStatus(
    undefined,
    currentConversation
  );
  const canReply = !isClosedStatus(undefined, currentConversation);
  const aiHandling = isAiHandlingConversation(currentConversation);
  const waitingOnUs = isNeedsReply(currentConversation);
  const phone = formatPhone(currentConversation.user_phone_number);
  const customerName = currentConversation.recipient_name || phone;
  const ticketSubject = getSubjectFromMessages();
  const historyTickets = currentConversation.history?.tickets || [];
  const historyTicketIds = new Set(historyTickets.map((item) => String(item._id)));
  const currentId = String(currentConversation._id);
  const unmatchedCalls = (currentConversation.history?.calls || []).filter(
    (call) => {
      const ticketId = call.ticket_id ? String(call.ticket_id) : "";
      if (ticketId === currentId) return false;
      if (ticketId && historyTicketIds.has(ticketId)) return false;
      return true;
    }
  );
  const historyCards = [
    ...historyTickets.map((item) => {
      const channelCounts =
        item.channel_counts || countsFromMessages(item.messages);
      return {
        id: String(item._id),
        kind: "ticket" as const,
        title:
          item.summary ||
          (item.number ? `#${item.number}` : t("inbound.whatsapp.previousTicket")),
        status: item.status,
        at: item.last_interaction_at,
        messageCount: item.message_count || item.messages?.length || 0,
        messages: item.messages || [],
        channelCounts,
        primaryChannel: primaryChannelOf(
          channelCounts,
          item.channels_used,
          item.messages?.[0]?.channel
        ),
      };
    }),
    ...unmatchedCalls.map((call) => ({
      id: String(call._id),
      kind: "call" as const,
      title: call.summary || t("inbound.whatsapp.previousCall"),
      status: call.status || "",
      at: call.start_time,
      messageCount: 1,
      messages: [
        {
          content: call.summary || "",
          role: "assistant" as const,
          created_at: call.start_time,
          actor: "ai_agent" as const,
          channel: "call",
        },
      ],
      duration: formatCallDuration(call.duration),
      channelCounts: { call: 1 } as ChannelCounts,
      primaryChannel: "call",
    })),
  ].sort(
    (a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime()
  );
  const historyMessageCount = historyCards.reduce(
    (sum, card) => sum + (card.messageCount || card.messages.length || 0),
    0
  );
  const selectedStatus = statusOptions.some(
    (option) => option.value === threadDisplayStatus
  )
    ? threadDisplayStatus
    : TICKET_DISPLAY_STATUS.OPEN;

  const toggleHistoryCard = async (cardId: string, kind: "ticket" | "call") => {
    if (openHistId === cardId) {
      setOpenHistId(null);
      return;
    }
    setOpenHistId(cardId);
    const card = historyCards.find((item) => item.id === cardId);
    if (
      kind !== "ticket" ||
      histMessages[cardId] ||
      (card?.messages && card.messages.length > 0)
    ) {
      return;
    }
    setHistLoadingId(cardId);
    try {
      const res = await getWhatsappConversationById(cardId, false);
      const msgs: IWhatsappMessage[] = res.data?.data?.messages || [];
      setHistMessages((prev) => ({ ...prev, [cardId]: msgs }));
    } catch {
      setHistMessages((prev) => ({ ...prev, [cardId]: [] }));
    } finally {
      setHistLoadingId(null);
    }
  };

  const renderMessageBubble = (msg: IWhatsappMessage, index: number) => {
    const withChannel: IWhatsappMessage = {
      ...msg,
      channel:
        msg.channel ||
        (currentConversation.channels_used?.length === 1
          ? currentConversation.channels_used[0]
          : "whatsapp"),
    };
    const isAgent = !isCustomerMessage(withChannel);
    const isHuman = withChannel.actor === "human_agent";
    const status = getMessageStatus(withChannel);
    return (
      <MessageBubble
        key={`${withChannel.created_at || "msg"}-${index}`}
        $isAgent={isAgent}
      >
        <MessageMeta $isAgent={isAgent}>
          {!isAgent ? (
            <MetaChannel>{channelIconOf(withChannel.channel)}</MetaChannel>
          ) : null}
          <MetaText>{getMessageMeta(withChannel)}</MetaText>
          {status === "autonomous" ? (
            <StatusChip $kind="autonomous">
              {t("inbound.whatsapp.autonomous")}
            </StatusChip>
          ) : null}
          {status === "humanTakeover" ? (
            <StatusChip $kind="human">
              {t("inbound.whatsapp.humanTakeover")}
            </StatusChip>
          ) : null}
        </MessageMeta>
        <MessageContent
          $isAgent={isAgent}
          $isHuman={isHuman}
          $hasMedia={Boolean(withChannel.media?.length || withChannel.media_url)}
        >
          <ThreadMedia
            messageId={withChannel._id}
            items={withChannel.media}
            fallbackUrl={withChannel.media_url}
          />
          {withChannel.content}
        </MessageContent>
      </MessageBubble>
    );
  };

  return (
    <FlexRow $embedded={embedded}>
      <InboxStatusDropdownStyles />
      <MainContent $embedded={embedded}>
        {!embedded && (
          <BackButton onClick={() => navigate("/inbound")}>
            <LeftOutlined style={{ marginRight: 8 }} />
            {t("inbound.whatsapp.backToList")}
          </BackButton>
        )}

        <DetailCard $embedded={embedded}>
          <ConversationHeader>
            <IdentityRow>
              <IdentityText>
                <PhoneTitle>{phone || "—"}</PhoneTitle>
                {customerName && customerName !== phone ? (
                  <CustomerLine>{customerName}</CustomerLine>
                ) : null}
              </IdentityText>
              <HeaderActions>
                <Tooltip title={t("inbound.whatsapp.changeStatus")}>
                  <StatusSelectTrigger>
                    <StatusSelect
                      $colors={getStatusColor(selectedStatus)}
                      value={selectedStatus}
                      onChange={(value) => handleStatusChange(value as string)}
                      disabled={updatingStatus}
                      size="middle"
                      variant="borderless"
                      suffixIcon={null}
                      optionLabelProp="label"
                      popupClassName="inbox-status-dropdown"
                      popupMatchSelectWidth={false}
                      dropdownStyle={{ minWidth: 196 }}
                      aria-label={t("inbound.whatsapp.changeStatus")}
                    >
                      {statusOptions.map((option) => {
                        const colors = getStatusColor(option.value);
                        const isActive = option.value === selectedStatus;
                        return (
                          <Select.Option
                            key={option.value}
                            value={option.value}
                            label={
                              <StatusTrigger className="status-trigger" $colors={colors}>
                                <StatusDot $color={colors.text} />
                                <span>{option.label}</span>
                                {updatingStatus ? (
                                  <LoadingOutlined className="status-chevron" />
                                ) : (
                                  <DownOutlined className="status-chevron" />
                                )}
                              </StatusTrigger>
                            }
                          >
                            <StatusOption $active={isActive} $colors={colors}>
                              <StatusDot $color={colors.text} />
                              <span>{option.label}</span>
                              {isActive ? (
                                <StatusOptionCheck>
                                  <CheckOutlined />
                                </StatusOptionCheck>
                              ) : null}
                            </StatusOption>
                          </Select.Option>
                        );
                      })}
                    </StatusSelect>
                  </StatusSelectTrigger>
                </Tooltip>
              </HeaderActions>
            </IdentityRow>

            <TicketRow>
              <TicketChips>
                {ticketSubject ? (
                  <TicketSubject>{ticketSubject}</TicketSubject>
                ) : null}
                {currentConversation.createdAt ? (
                  <OpenedMeta>
                    {t("inbound.whatsapp.openedOn", {
                      date: formatShortDate(
                        currentConversation.createdAt,
                        i18n.language
                      ),
                    })}
                  </OpenedMeta>
                ) : null}
              </TicketChips>
            </TicketRow>
          </ConversationHeader>

          <MessagesContainer ref={messagesContainerRef}>
            {historyCards.length > 0 ? (
              <HistoryToggle
                type="button"
                onClick={() => {
                  setShowHistory((open) => {
                    if (open) setOpenHistId(null);
                    return !open;
                  });
                }}
              >
                <HistoryOutlined />
                <HistoryToggleLabel>
                  {showHistory
                    ? t("inbound.whatsapp.hideEarlierHistory")
                    : t("inbound.whatsapp.viewEarlierHistory")}
                </HistoryToggleLabel>
                <HistoryToggleMeta>
                  {t("inbound.whatsapp.historyMeta", {
                    count: historyCards.length,
                    tickets: historyCards.length,
                    messages: historyMessageCount,
                  })}
                </HistoryToggleMeta>
                {showHistory ? <UpOutlined /> : <DownOutlined />}
              </HistoryToggle>
            ) : null}

            {showHistory && historyCards.length > 0 ? (
              <HistoryList>
                {historyCards.map((card) => {
                  const display = normalizeDisplayStatus(card.status, card);
                  const failed = display === TICKET_DISPLAY_STATUS.FAILED;
                  const closed =
                    display === TICKET_DISPLAY_STATUS.COMPLETED ||
                    String(card.status).toLowerCase() === "closed";
                  const open = openHistId === card.id;
                  const cardMessages = histMessages[card.id] || card.messages;
                  return (
                    <HistoryCard key={`${card.kind}-${card.id}`}>
                      <HistoryCardHead
                        type="button"
                        $open={open}
                        onClick={() => toggleHistoryCard(card.id, card.kind)}
                      >
                        <HistoryCardIcon>
                          {channelIconOf(card.primaryChannel)}
                        </HistoryCardIcon>
                        <HistoryCopy>
                          <HistoryKindRow>
                            <HistoryKind>{card.title}</HistoryKind>
                            <HistoryDate>
                              {formatShortDate(card.at, i18n.language)}
                            </HistoryDate>
                          </HistoryKindRow>
                          <HistorySummary>
                            {formatChannelMix(card.channelCounts) ||
                              t("inbound.whatsapp.messageCount", {
                                count: card.messageCount,
                              })}
                          </HistorySummary>
                        </HistoryCopy>
                        <HistoryTag $failed={failed} $closed={closed && !failed}>
                          {failed
                            ? t("inbound.whatsapp.statusFailed")
                            : closed
                              ? t("inbound.whatsapp.statusClosed")
                              : getStatusLabel(card.status)}
                        </HistoryTag>
                        {open ? <UpOutlined /> : <DownOutlined />}
                      </HistoryCardHead>
                      {open ? (
                        <HistoryCardBody>
                          {histLoadingId === card.id ? (
                            <HistorySummary>
                              <LoadingOutlined />
                            </HistorySummary>
                          ) : cardMessages.length === 0 ? (
                            <HistorySummary>
                              {t("inbound.whatsapp.noMessages")}
                            </HistorySummary>
                          ) : (
                            cardMessages.map((msg, index) => (
                              <HistoryItem
                                key={`${card.id}-${msg.created_at || index}`}
                              >
                                <HistoryCardIcon>
                                  {channelIconOf(msg.channel)}
                                </HistoryCardIcon>
                                <HistoryItemCopy>
                                  <HistoryWho>
                                    {getMessageMeta(
                                      msg,
                                      card.kind === "call"
                                        ? card.duration
                                        : undefined
                                    )}
                                  </HistoryWho>
                                  {msg.content ? (
                                    <HistoryText>{msg.content}</HistoryText>
                                  ) : null}
                                  <ThreadMedia
                                    messageId={msg._id}
                                    items={msg.media}
                                    fallbackUrl={msg.media_url}
                                  />
                                </HistoryItemCopy>
                              </HistoryItem>
                            ))
                          )}
                        </HistoryCardBody>
                      ) : null}
                    </HistoryCard>
                  );
                })}
              </HistoryList>
            ) : null}

            {displayMessages.length > 0 ? (
              <LatestDivider>
                <LatestLine />
                <LatestLabel>
                  {t("inbound.whatsapp.latestTicket", {
                    date: formatLongDate(
                      currentConversation.createdAt,
                      i18n.language
                    ),
                  })}
                </LatestLabel>
                <LatestLine />
              </LatestDivider>
            ) : null}

            <MessagesWrapper>
              {displayMessages.length === 0 ? (
                <EmptyThread>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("inbound.whatsapp.noMessages")}
                  />
                </EmptyThread>
              ) : (
                displayMessages.map((msg, index) =>
                  renderMessageBubble(msg, index)
                )
              )}
            </MessagesWrapper>
          </MessagesContainer>

          {aiHandling ? (
            <TakeOverPanel>
              <TakeOverCopy>
                <TakeOverTitle>
                  <UserSwitchOutlined />
                  {t("inbound.whatsapp.aiHandlingTitle")}
                </TakeOverTitle>
                <TakeOverHint>{t("inbound.whatsapp.aiHandlingHint")}</TakeOverHint>
              </TakeOverCopy>
              <TakeOverButton
                type="primary"
                icon={<UserSwitchOutlined />}
                onClick={handleTakeOver}
                loading={updatingStatus}
                disabled={updatingStatus}
              >
                {t("inbound.whatsapp.takeOver")}
              </TakeOverButton>
            </TakeOverPanel>
          ) : canReply ? (
            <ReplySection>
              {waitingOnUs && (
                <ReplyHint>{t("inbound.whatsapp.awaitingUs")}</ReplyHint>
              )}
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept={WHATSAPP_ALLOWED_FILE_TYPES.join(",")}
                onChange={handleFileSelect}
                multiple
              />
              {selectedFiles.length > 0 && (
                <FilePreviewList>
                  {selectedFiles.map((file) => {
                    const fileKey = `${file.name}-${file.size}`;
                    const preview = filePreviews.get(fileKey);
                    return (
                      <FilePreviewItem key={fileKey}>
                        <FilePreviewContent>
                          {preview ? (
                            <FilePreviewImage src={preview} alt="Preview" />
                          ) : (
                            <FilePreviewIcon>
                              {getFileIcon(file.type)}
                            </FilePreviewIcon>
                          )}
                          <FilePreviewInfo>
                            <FilePreviewName>{file.name}</FilePreviewName>
                            <FilePreviewSize>
                              {formatFileSize(file.size)}
                            </FilePreviewSize>
                          </FilePreviewInfo>
                        </FilePreviewContent>
                        <FileRemoveButton
                          onClick={() => removeFile(file)}
                          title={t("inbound.whatsapp.removeFile")}
                        >
                          <CloseOutlined />
                        </FileRemoveButton>
                      </FilePreviewItem>
                    );
                  })}
                </FilePreviewList>
              )}
              <ReplyInputContainer>
                <ReplyTextArea
                  ref={replyInputRef}
                  placeholder={t("inbound.whatsapp.typeMessage")}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (!sending) handleSendReply();
                    }
                  }}
                />
                <ReplyActions>
                  <ActionButtons>
                    <ActionButton
                      onClick={handleAttachClick}
                      title={t("inbound.whatsapp.attachFile")}
                    >
                      <PaperClipOutlined />
                    </ActionButton>
                  </ActionButtons>
                  <SendButton
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendReply}
                    disabled={
                      (!replyMessage.trim() && selectedFiles.length === 0) ||
                      sending
                    }
                    loading={sending}
                  >
                    {t("inbound.whatsapp.send")}
                  </SendButton>
                </ReplyActions>
              </ReplyInputContainer>
            </ReplySection>
          ) : (
            <ClosedBanner>
              {t("inbound.whatsapp.ticketClosed")}
            </ClosedBanner>
          )}
        </DetailCard>
      </MainContent>
    </FlexRow>
  );
};

const FlexRow = styled.div<{ $embedded?: boolean }>`
  display: flex;
  flex: ${({ $embedded }) => ($embedded ? 1 : "none")};
  height: ${({ $embedded }) => ($embedded ? "auto" : "100%")};
  min-height: ${({ $embedded }) => ($embedded ? "0" : "calc(100dvh - 56px)")};
  min-width: 0;
  width: 100%;
  background-color: ${({ $embedded }) =>
    $embedded ? "transparent" : theme.colors.grey3};

  @media (min-width: 901px) {
    min-height: ${({ $embedded }) => ($embedded ? "0" : "100vh")};
  }
`;

const MainContent = styled.div<{ $embedded?: boolean }>`
  flex: 1;
  padding: ${({ $embedded }) => ($embedded ? "0" : "32px 32px 20px")};
  gap: ${({ $embedded }) => ($embedded ? "0" : "16px")};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  min-width: 0;

  @media (max-width: 900px) {
    padding: ${({ $embedded }) => ($embedded ? "0" : "16px")};
  }
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackButton = styled.div`
  color: ${theme.colors.blue1};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;

const DetailCard = styled(Card)<{ $embedded?: boolean }>`
  border-radius: 12px;
  border: ${({ $embedded }) =>
    $embedded ? "none" : `1px solid ${theme.colors.grey2}`};
  box-shadow: ${({ $embedded }) =>
    $embedded ? "none" : "0 1px 2px rgba(0, 0, 0, 0.09)"};
  display: flex;
  flex-direction: column;
  flex: 1;
  height: auto;
  overflow: hidden;
  min-height: 0;
  min-width: 0;

  .ant-card-body {
    padding: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }
`;

const ConversationHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${theme.colors.grey2};
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  flex-shrink: 0;

  @media (max-width: 640px) {
    padding: 12px 14px;
    gap: 10px;
  }
`;

const HistoryToggle = styled.button`
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  color: #111;
  flex-shrink: 0;

  .anticon {
    font-size: 16px;
    color: #111;
    flex-shrink: 0;
  }
`;

const HistoryToggleLabel = styled.span`
  font-size: 16px;
  line-height: 1;
  color: #111;
`;

const HistoryToggleMeta = styled.span`
  margin-left: auto;
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.grey1};
  white-space: nowrap;
`;

const HistoryList = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  flex-shrink: 0;
`;

const HistoryCard = styled.div`
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  overflow: hidden;
`;

const HistoryCardHead = styled.button<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 12px 14px;
  border: none;
  background: ${({ $open }) => ($open ? theme.colors.grey3 : "#fff")};
  text-align: left;
  cursor: pointer;

  .anticon {
    font-size: 14px;
    color: #111;
    flex-shrink: 0;
  }
`;

const HistoryCardIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  color: #111;
  flex-shrink: 0;

  .anticon {
    font-size: 16px;
  }
`;

const HistoryCopy = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HistoryKindRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
`;

const HistoryKind = styled.div`
  font-size: 16px;
  line-height: 1.2;
  color: #111;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

const HistoryDate = styled.span`
  font-size: 14px;
  line-height: 1.2;
  color: ${theme.colors.grey1};
  white-space: nowrap;
  flex-shrink: 0;
`;

const HistorySummary = styled.div`
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.grey1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HistoryTag = styled.span<{ $failed?: boolean; $closed?: boolean }>`
  font-size: 14px;
  line-height: 1;
  padding: 4px 9px;
  border-radius: 6px;
  border: 1px solid ${theme.colors.grey2};
  white-space: nowrap;
  background: ${({ $failed, $closed }) =>
    $failed ? "#FFEBEE" : $closed ? "#fff" : theme.colors.green2};
  color: ${({ $failed, $closed }) =>
    $failed ? theme.colors.red1 : $closed ? theme.colors.grey1 : theme.colors.green1};
`;

const HistoryCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-top: 1px solid ${theme.colors.grey2};
  background: ${theme.colors.grey3};
`;

const HistoryItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const HistoryItemCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const HistoryWho = styled.span`
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.grey1};
`;

const HistoryText = styled.span`
  font-size: 16px;
  line-height: 1.45;
  color: #111;
  overflow-wrap: anywhere;
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
`;

const IdentityText = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  flex: 1 1 180px;
`;

const PhoneTitle = styled.span`
  font-weight: 600;
  font-size: 18px;
  line-height: 1.2;
  color: #111;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CustomerLine = styled.span`
  font-size: 16px;
  line-height: 1.2;
  color: #000;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderActions = styled.div`
  flex-shrink: 0;
`;

const StatusSelectTrigger = styled.span`
  display: inline-flex;
  cursor: pointer;
`;

const TicketRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
`;

const TicketChips = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

const TicketSubject = styled.div`
  flex: 1 1 120px;
  font-size: 14px;
  line-height: 1.4;
  color: #111;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;

  span {
    display: inline;
  }
`;

const OpenedMeta = styled.span`
  margin-left: auto;
  font-size: 14px;
  line-height: 1.2;
  color: ${theme.colors.grey1};
  white-space: nowrap;
  flex-shrink: 0;
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const StatusTrigger = styled.span<{
  $colors: { text: string; background: string };
}>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${({ $colors }) => $colors.background};
  color: ${({ $colors }) => $colors.text};
  border: 1px solid ${({ $colors }) => `${$colors.text}2E`};
  border-radius: 999px;
  padding: 7px 10px 7px 12px;
  font-size: 13px;
  line-height: 1;
  white-space: nowrap;
  font-weight: 600;
  cursor: pointer;
  transition: box-shadow 0.15s ease, filter 0.15s ease, border-color 0.15s ease;

  .status-chevron {
    font-size: 10px;
    color: ${({ $colors }) => $colors.text};
    opacity: 0.85;
    transition: transform 0.15s ease;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: ${theme.colors.grey3};
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MessagesWrapper = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LatestDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const LatestLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${theme.colors.grey2};
`;

const LatestLabel = styled.span`
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.grey1};
  white-space: nowrap;
`;

const EmptyThread = styled.div`
  margin: auto 0;
  padding: 48px 0;
`;

const MessageBubble = styled.div<{ $isAgent: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: ${({ $isAgent }) => ($isAgent ? "flex-end" : "flex-start")};
  align-self: ${({ $isAgent }) => ($isAgent ? "flex-end" : "flex-start")};
  max-width: 74%;
  min-width: 0;

  @media (max-width: 900px) {
    max-width: 92%;
  }
`;

const MessageMeta = styled.div<{ $isAgent: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
`;

const MetaChannel = styled.span`
  display: flex;
  align-items: center;
  color: #111;
  flex-shrink: 0;

  .anticon {
    font-size: 14px;
  }
`;

const MetaText = styled.span`
  font-size: 14px;
  line-height: 1;
  color: ${theme.colors.grey1};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusChip = styled.span<{ $kind: "autonomous" | "human" }>`
  flex-shrink: 0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.2;
  background: ${({ $kind }) =>
    $kind === "autonomous" ? theme.colors.green2 : theme.colors.yellow1};
  color: ${({ $kind }) => ($kind === "autonomous" ? theme.colors.green1 : "#000")};
`;

const MessageContent = styled.div<{
  $isAgent: boolean;
  $isHuman: boolean;
  $hasMedia?: boolean;
}>`
  background: ${({ $isAgent, $isHuman }) =>
    $isAgent && !$isHuman ? theme.colors.green2 : "#fff"};
  border: 1px solid ${theme.colors.grey2};
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 16px;
  line-height: 1.5;
  color: #111;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  width: fit-content;
  min-width: ${({ $hasMedia }) => ($hasMedia ? "260px" : "0")};
  max-width: 100%;
`;

const ReplySection = styled.div`
  border-top: 1px solid ${theme.colors.grey2};
  padding: 16px 20px;
  background: #fff;
  flex-shrink: 0;

  @media (max-width: 640px) {
    padding: 12px;
  }
`;

const ReplyHint = styled.div`
  font-size: 13px;
  color: #92400e;
  margin-bottom: 8px;
`;

const TakeOverPanel = styled.div`
  border-top: 1px solid ${theme.colors.grey2};
  padding: 16px 20px;
  background: ${theme.colors.green2};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    padding: 14px 16px;
  }
`;

const TakeOverCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const TakeOverTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.green1};

  .anticon {
    font-size: 16px;
  }
`;

const TakeOverHint = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: #3f6f64;
`;

const TakeOverButton = styled(Button)`
  flex-shrink: 0;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  background: ${theme.colors.green1};
  color: #fff;
  box-shadow: none;

  &:hover,
  &:focus {
    background: #0d7d66 !important;
    color: #fff !important;
  }
`;

const ClosedBanner = styled.div`
  border-top: 1px solid ${theme.colors.grey2};
  padding: 16px 24px;
  background: ${theme.colors.grey4};
  color: ${theme.colors.grey1};
  font-size: 14px;
  text-align: center;
`;

const ReplyInputContainer = styled.div`
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  overflow: hidden;
`;

const ReplyTextArea = styled(TextArea)`
  border: none;
  resize: none;
  padding: 12px 16px;

  &:focus,
  &:hover {
    border: none;
    box-shadow: none;
  }
`;

const ReplyActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${theme.colors.grey4};
  border-top: 1px solid ${theme.colors.grey2};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${theme.colors.grey1};
  font-size: 18px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${theme.colors.grey2};
    color: #333;
  }
`;

const SendButton = styled(Button)`
  background: ${theme.colors.green1};
  border-color: ${theme.colors.green1};

  &:hover {
    background: ${theme.colors.green1} !important;
    border-color: ${theme.colors.green1} !important;
    opacity: 0.9;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FilePreviewList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  background: ${theme.colors.grey4};
  max-height: 200px;
  overflow-y: auto;
`;

const FilePreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  max-width: 280px;
`;

const FilePreviewContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const FilePreviewImage = styled.img`
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid ${theme.colors.grey2};
  flex-shrink: 0;
`;

const FilePreviewIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.grey2};
  border-radius: 4px;
  font-size: 18px;
  color: ${theme.colors.grey1};
  flex-shrink: 0;
`;

const FilePreviewInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FilePreviewName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FilePreviewSize = styled.div`
  font-size: 12px;
  color: ${theme.colors.grey1};
  margin-top: 2px;
`;

const FileRemoveButton = styled.button`
  background: ${theme.colors.grey2};
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${theme.colors.grey1};
  font-size: 12px;
  border-radius: 50%;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  &:hover {
    background: #ff4d4f;
    color: #fff;
  }
`;

const StatusSelect = styled(Select)<{
  $colors: { text: string; background: string };
}>`
  width: fit-content !important;
  min-width: 0;

  .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    padding: 0 !important;
    height: auto !important;
    cursor: pointer !important;
  }

  .ant-select-selection-item {
    padding: 0 !important;
    padding-inline-end: 0 !important;
    line-height: 1 !important;
    height: auto !important;
    overflow: visible !important;
  }

  .ant-select-arrow {
    display: none !important;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector,
  &.ant-select-outlined:not(.ant-select-disabled):not(.ant-select-customize-input):not(.ant-pagination-size-changer):hover .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
  }

  &:hover .status-trigger {
    filter: brightness(0.97);
    box-shadow: 0 0 0 3px ${({ $colors }) => $colors.background};
  }

  &.ant-select-open .status-trigger {
    box-shadow: 0 0 0 3px ${({ $colors }) => $colors.background};
    border-color: ${({ $colors }) => `${$colors.text}59`};
  }

  &.ant-select-open .status-chevron {
    transform: rotate(180deg);
  }

  &.ant-select-disabled {
    opacity: 0.7;
  }
`;

const StatusOption = styled.div<{
  $active?: boolean;
  $colors: { text: string; background: string };
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  background: ${({ $active, $colors }) =>
    $active ? $colors.background : "transparent"};
  color: ${({ $colors }) => $colors.text};
  font-weight: 600;
  font-size: 13px;
  line-height: 1.2;

  .ant-select-item-option-active & {
    background: ${({ $colors }) => $colors.background};
  }
`;

const StatusOptionCheck = styled.span`
  margin-left: auto;
  font-size: 12px;
  display: inline-flex;
`;

const InboxStatusDropdownStyles = createGlobalStyle`
  .inbox-status-dropdown.ant-select-dropdown {
    padding: 6px;
    border-radius: 12px;
    border: 1px solid ${theme.colors.grey2};
    box-shadow: 0 10px 30px rgba(17, 24, 39, 0.12);
  }

  .inbox-status-dropdown .ant-select-item {
    padding: 0;
    border-radius: 8px;
    background: transparent !important;
  }

  .inbox-status-dropdown .ant-select-item-option-selected,
  .inbox-status-dropdown .ant-select-item-option-active {
    background: transparent !important;
    font-weight: 600;
  }
`;

export default WhatsappDetail;
